import { useState, useEffect, useCallback } from 'react';

export interface WeightProgressData {
  currentWeek: {
    weekKey: string;
    weekStart: string;
    weekEnd: string;
    average: number | null;
    entriesCount: number;
  };
  previousWeek: {
    average: number | null;
  };
  weeklyChange: number | null;
  startingWeight: number | null;
  goalWeight: number | null;
  journeyProgress: number;
  streak: {
    current: number;
    best: number;
  };
  timeToGoal: {
    status: 'reached' | 'on_track' | 'gaining' | 'insufficient_data';
    remaining: number;
    currentWeight?: number;
    goalWeight?: number;
    weeklyRate?: number | null;
    weeksUsed?: number;
    weeksAvailable?: number;
    weeksRemaining?: number | null;
    targetRate?: number | null;
    weeksAtPlan?: number | null;
  } | null;
}

interface UseWeightProgressResult {
  data: WeightProgressData | null;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
}

/**
 * Shared hook for weight progress, streak, and time-to-goal data.
 * Fetches from /api/health/weekly-rollups/me/weight-progress once
 * and exposes all data for WeightProgressTile, StreaksTile, TimeToGoalTile, and ClientHome.
 */
export function useWeightProgress(): UseWeightProgressResult {
  const [data, setData] = useState<WeightProgressData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const token = localStorage.getItem('auth_token');
      const response = await fetch('/api/health/weekly-rollups/me/weight-progress', {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': token ? `Bearer ${token}` : ''
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch weight progress data');
      }

      const result = await response.json();
      if (!result.success) {
        throw new Error(result.error || 'Failed to load weight progress');
      }

      setData(result);
    } catch (err) {
      console.error('Error loading weight progress:', err);
      setError(err instanceof Error ? err.message : 'Failed to load weight data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  return { data, loading, error, refresh: loadData };
}

export default useWeightProgress;
